import React from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Selection } from 'react-aria-components';
import { useApi } from '@/api/ApiContext';
import {
    getImageDisplayUrl,
    getImageStoragePath,
} from '@/api/handlers/resources/images';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CategoryTreeNode } from '@/api/models';
import {
    useCategoryDetailsQuery,
    useCategoryTreeQuery,
    useDeleteCategoryMutation,
    useUpdateCategoryMutation,
    useUploadImageMutation,
} from '@/api/queries';
import { useDeleteConfirm } from '@/components/deleteConfirmDialog';
import { useCreateCategory } from './useCreateCategory';
import {
    CATEGORIES_PAGE_SIZE,
    getPaginationWindow,
    type Category,
    type CategoryParentOption,
} from './components/categoriesMeta';
import {
    collectSubtreeIds,
    filterTree,
    findCategory,
    flattenForParentPicker,
    indexStatsById,
    mapTreeNodesToRows,
} from './components/categoryTree';
import type {
    CategoryFormInitialValues,
    CategoryFormSubmit,
} from './components/categoryFormModal/useCategoryForm';

export type UseCategoriesPageResult = {
    /** Drives the delete confirmation and surfaces a refusal inline. */
    deleteConfirm: ReturnType<typeof useDeleteConfirm>;
    attributesCategoryId: string | null;
    attributesCategoryName: string;
    attributesCategorySlug: string;
    data: Category[] | null;
    editingInitialValues: CategoryFormInitialValues | null;
    error: ErrorResponse | null;
    expandedKeys: Selection;
    formError: string | null;
    formMode: 'create' | 'edit';
    isAttributesOpen: boolean;
    isFormOpen: boolean;
    isLoading: boolean;
    isSubmitting: boolean;
    onAddCategory: () => void;
    onCloseAttributes: () => void;
    onCloseForm: () => void;
    onDeleteCategory: (id: string) => void;
    onEditCategory: (id: string) => void;
    onExpandedChange: (keys: Selection) => void;
    onManageAttributes: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onRetry: () => void;
    onSearchChange: (value: string) => void;
    onSubmitCategory: (input: CategoryFormSubmit) => void;
    page: number;
    pageSize: number;
    pageWindow: (number | 'ellipsis')[];
    parentOptions: CategoryParentOption[];
    search: string;
    totalCount: number;
    totalPages: number;
};

/** Query param that deep-links the attributes drawer to a category. */
const ATTRIBUTES_PARAM = 'attributes';

/**
 * Drives the Categories page — fetches the nested hierarchy via
 * `useCategoryTreeQuery`, maps it into expandable-row data, and routes
 * create/edit/delete through the matching mutations.
 *
 * The tree endpoint is unpaginated (the table needs the full hierarchy), so
 * paging is **client-side over the root rows**: the filtered roots are sliced
 * into pages while each root keeps its whole subtree. Search filters the tree
 * (path-preserving) and resets to page 1.
 *
 * Create is one multipart POST; edit needs the two-step image flow (upload via
 * `POST /images` first when a fresh image was picked, then `PUT`). All writes
 * invalidate the category queries.
 */
export const useCategoriesPage = (): UseCategoriesPageResult => {
    const { baseUrl } = useApi();
    const query = useCategoryTreeQuery();
    // `/tree` carries the hierarchy and no figures; `/details` carries the
    // figures and no hierarchy. The table needs both (BOS-137).
    const detailsQuery = useCategoryDetailsQuery();
    const { create, isCreating } = useCreateCategory();
    const updateMutation = useUpdateCategoryMutation();
    const deleteMutation = useDeleteCategoryMutation();
    const uploadMutation = useUploadImageMutation();

    const [search, setSearch] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(CATEGORIES_PAGE_SIZE);
    const [isFormOpen, setIsFormOpen] = React.useState(false);
    const [editingId, setEditingId] = React.useState<string | null>(null);
    const [formError, setFormError] = React.useState<string | null>(null);
    // Expansion the user has toggled by hand. While a search is active we
    // expand everything (below) so matches deep in the tree stay visible.
    const [userExpandedKeys, setUserExpandedKeys] = React.useState<Selection>(
        () => new Set<string>(),
    );

    const rawNodes: CategoryTreeNode[] | null = React.useMemo(() => {
        if (!query.data || !query.data.success) {
            return null;
        }
        return query.data.response.items;
    }, [query.data]);

    const statsById = React.useMemo(
        () =>
            detailsQuery.data?.success
                ? indexStatsById(detailsQuery.data.response.items)
                : indexStatsById([]),
        [detailsQuery.data],
    );

    // Map the nested nodes into rows once, resolving image URLs to absolute
    // for `<img>` rendering and merging each row's totals in by id. A parent's
    // figures are its own plus every descendant's.
    const rows: Category[] | null = React.useMemo(() => {
        if (rawNodes === null) {
            return null;
        }
        return mapTreeNodesToRows(
            rawNodes,
            (url) => (url ? (getImageDisplayUrl(url, baseUrl) ?? null) : null),
            statsById,
        );
    }, [baseUrl, rawNodes, statsById]);

    // The full filtered tree (path-preserving). Root count drives pagination.
    const filteredRoots = React.useMemo<Category[] | null>(() => {
        if (rows === null) {
            return null;
        }
        return filterTree(rows, search);
    }, [rows, search]);

    const totalItems = filteredRoots?.length ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    // Clamp so an out-of-range page (e.g. after a search shrinks the set) can
    // never produce a negative slice offset.
    const safePage = Math.min(Math.max(1, page), totalPages);

    // The current page of root rows — each keeps its whole subtree, so the
    // expandable tree still renders normally; we only paginate the top level.
    const data = React.useMemo<Category[] | null>(() => {
        if (filteredRoots === null) {
            return null;
        }
        const start = (safePage - 1) * pageSize;
        return filteredRoots.slice(start, start + pageSize);
    }, [filteredRoots, pageSize, safePage]);

    const pageWindow = React.useMemo(
        () => getPaginationWindow(totalPages, safePage),
        [safePage, totalPages],
    );

    const error: ErrorResponse | null =
        query.data && !query.data.success ? query.data.error : null;

    const editingRow = React.useMemo(() => {
        if (editingId === null || rows === null) {
            return null;
        }
        return findCategory(rows, editingId);
    }, [editingId, rows]);

    const editingInitialValues: CategoryFormInitialValues | null =
        React.useMemo(() => {
            if (editingRow === null) {
                return null;
            }
            return {
                name: editingRow.name,
                slug: editingRow.slug,
                parentId: editingRow.parentId,
                // Rows already carry display (absolute) image URLs.
                image: editingRow.imageUrl
                    ? {
                          imageName: editingRow.name,
                          imageUrl: editingRow.imageUrl,
                      }
                    : null,
            };
        }, [editingRow]);

    // Parent picker options: the whole tree flattened, minus the category being
    // edited and its descendants (a node can't be re-parented under itself).
    const parentOptions = React.useMemo<CategoryParentOption[]>(() => {
        if (rows === null) {
            return [];
        }
        const excluded = editingRow
            ? new Set(collectSubtreeIds(editingRow))
            : new Set<string>();
        return flattenForParentPicker(rows, excluded);
    }, [editingRow, rows]);

    // Attributes drawer is deep-linked via `?attributes={categoryId}` so it
    // survives refresh/share. The category id lives entirely in the URL — the
    // name/slug for the header are resolved from the loaded rows when present.
    const [searchParams, setSearchParams] = useSearchParams();
    // A bare `?attributes=` (empty/whitespace) is not a real selection — treat
    // it as closed so the drawer never opens on, or fetches for, an empty id.
    const rawAttributesParam = searchParams.get(ATTRIBUTES_PARAM);
    const attributesCategoryId =
        rawAttributesParam && rawAttributesParam.trim()
            ? rawAttributesParam
            : null;

    const attributesCategory = React.useMemo(() => {
        if (attributesCategoryId === null || rows === null) {
            return null;
        }
        return findCategory(rows, attributesCategoryId);
    }, [attributesCategoryId, rows]);

    const onManageAttributes = React.useCallback(
        (id: string) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set(ATTRIBUTES_PARAM, id);
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const onCloseAttributes = React.useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete(ATTRIBUTES_PARAM);
                return next;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    // While searching, force every kept branch open so matches are visible.
    const expandedKeys: Selection = search.trim() ? 'all' : userExpandedKeys;

    const onExpandedChange = React.useCallback((keys: Selection) => {
        setUserExpandedKeys(keys);
    }, []);

    const onSearchChange = React.useCallback((value: string) => {
        setSearch(value);
        // A changed filter resets to the first page of the new root set.
        setPage(1);
    }, []);

    const onPageChange = React.useCallback((next: number) => {
        setPage(next);
    }, []);

    const onPageSizeChange = React.useCallback((next: number) => {
        setPageSize(next);
        setPage(1);
    }, []);

    const onAddCategory = React.useCallback(() => {
        setEditingId(null);
        setFormError(null);
        setIsFormOpen(true);
    }, []);

    const onEditCategory = React.useCallback((id: string) => {
        setEditingId(id);
        setFormError(null);
        setIsFormOpen(true);
    }, []);

    const onCloseForm = React.useCallback(() => {
        setIsFormOpen(false);
        setEditingId(null);
        setFormError(null);
    }, []);

    // The dialog owns the refusal: a delete can now come back with a
    // sentence naming what depends on the record (bos-api BOS-121).
    const deleteConfirm = useDeleteConfirm({
        onDelete: (id) => deleteMutation.mutateAsync(id),
    });

    const onDeleteCategory = React.useCallback(
        (id: string) => {
            const item = data?.find((row) => row.id === id);
            deleteConfirm.onRequestDelete({ id, name: item?.name ?? id });
        },
        [data, deleteConfirm],
    );

    const onRetry = React.useCallback(() => {
        query.refetch();
    }, [query]);

    const onSubmitCategory = React.useCallback(
        async (input: CategoryFormSubmit) => {
            setFormError(null);

            if (editingId === null) {
                // ---- Create flow (single multipart round-trip) -------------
                // Shared with the Control Center's "Add category", which opens
                // this same form from the launcher — see `useCreateCategory`.
                const created = await create(input);
                if (!created.ok) {
                    setFormError(created.message);
                    return;
                }
                setIsFormOpen(false);
                return;
            }

            // ---- Edit flow ------------------------------------------------
            // The form seeds its image field with the display (API-host
            // absolute) URL for `<img>` rendering; normalize it back to the
            // server-relative `/storage/...` path so we don't persist an
            // environment-specific host. (Fresh uploads below already return a
            // relative path.)
            let nextImageUrl: string | null =
                getImageStoragePath(input.imageUrl, baseUrl) ?? null;
            if (input.imageUpload) {
                const uploadResult = await uploadMutation.mutateAsync({
                    payload: input.imageUpload,
                    purpose: 'categories',
                });
                if (!uploadResult.success) {
                    setFormError(
                        uploadResult.error.message ?? 'Failed to upload image.',
                    );
                    return;
                }
                nextImageUrl = uploadResult.response.imageUrl;
            }

            const updateResult = await updateMutation.mutateAsync({
                id: editingId,
                body: {
                    name: input.name,
                    slug: input.slug,
                    imageUrl: nextImageUrl,
                    parentId: input.parentId,
                },
            });
            if (!updateResult.success) {
                setFormError(
                    updateResult.error.message ?? 'Failed to update category.',
                );
                return;
            }
            setEditingId(null);
            setIsFormOpen(false);
        },
        [baseUrl, create, editingId, updateMutation, uploadMutation],
    );

    return {
        deleteConfirm,
        attributesCategoryId,
        attributesCategoryName: attributesCategory?.name ?? '',
        attributesCategorySlug: attributesCategory?.slug ?? '',
        data,
        editingInitialValues,
        error,
        expandedKeys,
        formError,
        formMode: editingId === null ? 'create' : 'edit',
        isAttributesOpen: attributesCategoryId !== null,
        isFormOpen,
        isLoading: query.isLoading,
        isSubmitting:
            isCreating || updateMutation.isPending || uploadMutation.isPending,
        onAddCategory,
        onCloseAttributes,
        onCloseForm,
        onDeleteCategory,
        onEditCategory,
        onExpandedChange,
        onManageAttributes,
        onPageChange,
        onPageSizeChange,
        onRetry,
        onSearchChange,
        onSubmitCategory,
        page: safePage,
        pageSize,
        pageWindow,
        parentOptions,
        search,
        totalCount: totalItems,
        totalPages,
    };
};
